// Badge type exposed by the public profile API. The catalog itself
// lives server-side (api/src/lib/badges.ts) — the web client only
// renders whatever the server hands back, so a slug going stale or a
// new badge being added is a server-only change.

export interface BadgeProgress {
  /// Current value toward the badge target. For `kind: 'count'` this
  /// is a count (e.g. 187 solves toward a 250 threshold). For
  /// `kind: 'time-ms'` this is the user's best time at that difficulty
  /// in milliseconds — lower is better.
  current: number;
  target: number;
  kind: 'count' | 'time-ms';
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  /// ISO 8601 timestamp when the user earned this badge, OR null when
  /// the user hasn't earned it yet (server returns the full catalog so
  /// locked badges can be rendered alongside earned ones).
  earnedAt: string | null;
  /// Per-badge progress for the hover tooltip. Older API responses may
  /// omit this; renderers should fall back gracefully.
  progress?: BadgeProgress | null;
}

/// Formats a BadgeProgress as a human-readable single line for use in
/// `title` tooltips. Returns null for missing/zero-target progress so
/// the tooltip can just show the description.
export function formatBadgeProgress(p: BadgeProgress | null | undefined): string | null {
  if (!p || p.target <= 0) return null;
  if (p.kind === 'time-ms') {
    // Format best time as M:SS (or "—" if user has no solves at that
    // difficulty yet; the server uses target+1 as a sentinel for that).
    const sentinel = p.current > p.target && p.current === p.target + 1;
    const bestLabel = sentinel ? '—' : formatMs(p.current);
    return `Progress: best ${bestLabel} / target under ${formatMs(p.target)}`;
  }
  // count-kind
  const cur = Math.min(p.current, p.target);
  return `Progress: ${cur.toLocaleString()} / ${p.target.toLocaleString()}`;
}

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
